import React from "react";
import type { AnalyticsDiscoveryTarget, AnalyticsSurface } from "@/main/analytics/analyticsEvents";
import { reportDiscoveryRailShown } from "@/main/analytics/discoveryAttribution";

/**
 * The fire-once guard for `discovery_rail_shown` — one impression per rail module per page instance.
 *
 * <p>The guard is a ref, not state, so re-renders cannot re-fire it and firing cannot cause a render.
 * That matters more than it looks: the rails live in the home and discussion islands, and an impression
 * that fired per render would both inflate the denominator (reading as a CTR regression the day ranking
 * changes) and add a write to a subtree the persistent chrome subscribes to.
 *
 * <p>`ready` must mean "open, resolved, and non-empty" — a loading, errored, collapsed or empty module
 * is not an impression. `reportDiscoveryRailShown` drops a zero `itemCount` as a second line of defence.
 *
 * <p>Design: designs/01-now/2026-08-07-recommendation-surface-measurement-design.md.
 */
export function useDiscoveryRailImpression(options: {
    targetType: AnalyticsDiscoveryTarget;
    surface: AnalyticsSurface;
    itemCount: number;
    /** The module is expanded AND its data has resolved (not loading, no error). */
    ready: boolean;
}): void {
    const { targetType, surface, itemCount, ready } = options;
    const hasFiredRef = React.useRef(false);

    React.useEffect(() => {
        if (hasFiredRef.current || !ready || itemCount <= 0) {
            return;
        }

        hasFiredRef.current = true;
        reportDiscoveryRailShown({ targetType, surface, itemCount });
    }, [ready, itemCount, targetType, surface]);
}
